import { parse as parseYaml, parseDocument } from 'yaml';

export interface FrontmatterResult {
  frontmatter: Record<string, unknown> | null;
  body: string;
  preamble: string;
  error?: string;
}

export interface FrontmatterSpliceResult {
  content: string;
  error?: string;
}

/**
 * Splits a markdown document into its `---`-delimited frontmatter block plus
 * the surrounding preamble/body, WITHOUT parsing the YAML. Returns the raw
 * YAML string so a caller can do a formatting-preserving edit (via the `yaml`
 * Document API) rather than a lossy parse -> object -> re-stringify round-trip.
 */
function splitFrontmatterBlock(
  content: string,
):
  | { ok: true; preamble: string; yaml: string; body: string }
  | { ok: false; error: string } {
  const lines = content.split('\n');
  let startLine = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i]!.trim() === '---') {
      startLine = i;
      break;
    }
  }
  if (startLine === -1) {
    return { ok: false, error: 'No YAML frontmatter found (missing ---)' };
  }
  let endLine = -1;
  for (let i = startLine + 1; i < lines.length; i++) {
    if (lines[i]!.trim() === '---') {
      endLine = i;
      break;
    }
  }
  if (endLine === -1) {
    return { ok: false, error: 'Unterminated frontmatter - missing closing ---' };
  }
  return {
    ok: true,
    preamble: lines.slice(0, startLine).join('\n').trim(),
    yaml: lines.slice(startLine + 1, endLine).join('\n'),
    body: lines.slice(endLine + 1).join('\n').trim(),
  };
}

/**
 * Sets (or deletes, when `value === undefined`) a nested field in a markdown
 * document's YAML frontmatter, preserving the rest of the document verbatim -
 * the human-authored body, the preamble title, and every untouched frontmatter
 * field's formatting/ordering/type (e.g. unquoted date strings are NOT coerced
 * to Date objects the way a full parse+stringify would).
 *
 * ENG-7351: used to merge `multi_agent.slack_peers` into an agent's CHARTER
 * without regenerating the whole document (there is no round-trip charter
 * generator - `generateCharterMd` is one-way and would discard the body).
 *
 * `path` is the key path into the frontmatter mapping, e.g.
 * `['multi_agent', 'slack_peers']`. Intermediate mappings are created as
 * needed. Returns the rewritten document (or `error` if the frontmatter is
 * missing/unterminated - the caller decides whether that is fatal).
 */
export function setFrontmatterField(
  content: string,
  path: ReadonlyArray<string>,
  value: unknown,
): FrontmatterSpliceResult {
  if (path.length === 0) {
    return { content, error: 'setFrontmatterField requires a non-empty path' };
  }
  const split = splitFrontmatterBlock(content);
  if (!split.ok) {
    return { content, error: split.error };
  }

  let doc;
  try {
    doc = parseDocument(split.yaml);
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Unknown YAML parse error';
    return { content, error: `YAML parse error: ${message}` };
  }
  if (doc.errors.length > 0) {
    return { content, error: `YAML parse error: ${doc.errors[0]!.message}` };
  }

  const keys = [...path];
  try {
    if (value === undefined) {
      // deleteIn throws if an intermediate collection is absent; only delete
      // when the full path actually exists (a no-op clear is fine).
      if (doc.hasIn(keys)) doc.deleteIn(keys);
    } else {
      doc.setIn(keys, value);
    }
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Unknown YAML edit error';
    return { content, error: `Could not edit frontmatter: ${message}` };
  }

  // Reassemble preamble + edited frontmatter + body. doc.toString() ends with a
  // trailing newline; trim it so the delimiters sit flush.
  const editedYaml = doc.toString().replace(/\n+$/, '');
  const out: string[] = [];
  if (split.preamble) out.push(split.preamble, '');
  out.push('---', editedYaml, '---');
  if (split.body) out.push('', split.body);
  return { content: out.join('\n') + '\n' };
}

/**
 * Extracts YAML frontmatter from a markdown document.
 * Frontmatter is delimited by `---` on its own line. It may appear at the
 * start of the document or after a preamble (e.g., a `# Title` line).
 */
export function extractFrontmatter(content: string): FrontmatterResult {
  // Find the first --- on its own line
  const lines = content.split('\n');
  let startLine = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i]!.trim() === '---') {
      startLine = i;
      break;
    }
  }

  if (startLine === -1) {
    return { frontmatter: null, body: content, preamble: '', error: 'No YAML frontmatter found (missing ---)' };
  }

  // Find the closing ---
  let endLine = -1;
  for (let i = startLine + 1; i < lines.length; i++) {
    if (lines[i]!.trim() === '---') {
      endLine = i;
      break;
    }
  }

  if (endLine === -1) {
    return { frontmatter: null, body: content, preamble: '', error: 'Unterminated frontmatter — missing closing ---' };
  }

  const preamble = lines.slice(0, startLine).join('\n').trim();
  const yamlStr = lines.slice(startLine + 1, endLine).join('\n').trim();
  const body = lines.slice(endLine + 1).join('\n').trim();

  if (!yamlStr) {
    return { frontmatter: null, body, preamble, error: 'Empty frontmatter block' };
  }

  try {
    const parsed = parseYaml(yamlStr);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return { frontmatter: null, body, preamble, error: 'Frontmatter must be a YAML mapping (object)' };
    }
    return { frontmatter: parsed as Record<string, unknown>, body, preamble };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Unknown YAML parse error';
    return { frontmatter: null, body, preamble, error: `YAML parse error: ${message}` };
  }
}
